import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../../../context/API';
import { PopularItemsData, FetchPopularItemsParams } from '../types';

interface PopularItemsState {
  popularItemsData: PopularItemsData | null;
  loading: boolean;
  error: string | null;
}

const initialState: PopularItemsState = {
  popularItemsData: null,
  loading: false,
  error: null,
};

export const fetchPopularItems = createAsyncThunk<
  PopularItemsData,
  FetchPopularItemsParams,
  { rejectValue: string }
>(
  'popularItems/fetchPopularItems',
  async (params, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await API.get<{ success: boolean; data: PopularItemsData }>(
        '/analytics/popular-items',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            startDate: params.startDate,
            endDate: params.endDate,
            merchantId: params.merchantId,
            store: params.store,
            limit: params.limit,
            ...(params.categoryId && { categoryId: params.categoryId }),
          },
        }
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        return rejectWithValue('Failed to fetch popular items data');
      }
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch popular items data'
      );
    }
  }
);

const popularItemsSlice = createSlice({
  name: 'popularItems',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearPopularItemsData: (state) => {
      state.popularItemsData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPopularItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPopularItems.fulfilled, (state, action) => {
        state.loading = false;
        state.popularItemsData = action.payload;
      })
      .addCase(fetchPopularItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearPopularItemsData } = popularItemsSlice.actions;
export default popularItemsSlice.reducer;
